/**
 * Pixel REAL de la cuenta "ReservaManager Ads" (creado el 17-sep-2026). El
 * valor anterior (1197111605354432) era el ID de la APP de Meta, no un Pixel:
 * el Pixel nunca llegó a registrar nada. No cambiar sin comprobarlo en
 * Business Manager → Orígenes de datos.
 */
const META_PIXEL_ID =
  import.meta.env.VITE_META_PIXEL_ID?.trim() || "1479250850925533";
const COOKIE_SETTINGS_KEY = "rm_cookie_settings";

let pixelLoaded = false;

type FbqFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

function readSharedSettings(): { marketing?: boolean } | null {
  try {
    const local = localStorage.getItem(COOKIE_SETTINGS_KEY);
    if (local) return JSON.parse(local) as { marketing?: boolean };

    const prefix = `${COOKIE_SETTINGS_KEY}=`;
    const raw = document.cookie
      .split(";")
      .map((value) => value.trim())
      .find((value) => value.startsWith(prefix))
      ?.slice(prefix.length);
    return raw
      ? (JSON.parse(decodeURIComponent(raw)) as { marketing?: boolean })
      : null;
  } catch {
    return null;
  }
}

export function hasMarketingConsent(): boolean {
  return readSharedSettings()?.marketing === true;
}

function installFbqStub(): FbqFunction {
  if (window.fbq) return window.fbq;

  const fbq: FbqFunction = (...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      (fbq.queue ||= []).push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
}

function loadMetaPixel() {
  if (!META_PIXEL_ID || pixelLoaded || !hasMarketingConsent()) return;

  const fbq = installFbqStub();

  if (!document.querySelector('script[src*="connect.facebook.net/en_US/fbevents.js"]')) {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  fbq("init", META_PIXEL_ID);
  fbq("track", "PageView");
  pixelLoaded = true;
}

/**
 * Inicializa Meta Pixel únicamente cuando existe consentimiento de marketing.
 * El ID se puede sobreescribir mediante VITE_META_PIXEL_ID en el proyecto dashboard.
 */
export function initMetaPixel() {
  loadMetaPixel();

  const handler = (event: Event) => {
    const settings = (event as CustomEvent<{ marketing?: boolean }>).detail;
    if (settings?.marketing) loadMetaPixel();
  };

  window.addEventListener("cookieConsentChanged", handler);
  return () => window.removeEventListener("cookieConsentChanged", handler);
}

/**
 * Envía un evento estándar/personalizado solo si Meta Pixel está activo. Con
 * `eventId`, Meta lo deduplica con el mismo evento enviado por el servidor
 * (Conversions API).
 */
export function trackMetaEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
  eventId?: string,
) {
  if (!hasMarketingConsent()) return;
  loadMetaPixel();
  if (eventId) window.fbq?.("track", eventName, params || {}, { eventID: eventId });
  else window.fbq?.("track", eventName, params || {});
}
